import React, { useEffect, useState, createElement } from 'react';
import { XIcon, PlusIcon, TrashIcon, MonitorIcon, UsersIcon } from 'lucide-react';
import { Consultant } from '../Consultants';
import { useConsultants } from './ConsultantsContext';
interface ConsultantFormProps {
  consultant: Consultant | null;
  onClose: () => void;
}
export function ConsultantForm({
  consultant,
  onClose
}: ConsultantFormProps) {
  const {
    addConsultant,
    updateConsultant
  } = useConsultants();
  const [formData, setFormData] = useState<Partial<Consultant>>({
    name: '',
    title: '',
    image: '',
    specialization: [],
    rating: 5.0,
    reviewCount: 0,
    experience: '',
    bio: '',
    availability: {
      days: ['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس'],
      hours: ['صباحاً', 'مساءً']
    },
    price: {
      inPerson: '',
      online: ''
    },
    location: '',
    duration: '',
    languages: ['العربية'],
    discount: '',
    badges: [],
    onlineAvailable: true,
    inPersonAvailable: true
  });
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newBadge, setNewBadge] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (consultant) {
      setFormData(consultant);
    }
  }, [consultant]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'onlinePrice') {
      setFormData({
        ...formData,
        price: {
          ...formData.price,
          online: value
        }
      });
    } else if (name === 'inPersonPrice') {
      setFormData({
        ...formData,
        price: {
          ...formData.price,
          inPerson: value
        }
      });
    } else if (name === 'onlineAvailable') {
      setFormData({
        ...formData,
        onlineAvailable: (e.target as HTMLInputElement).checked
      });
    } else if (name === 'inPersonAvailable') {
      setFormData({
        ...formData,
        inPersonAvailable: (e.target as HTMLInputElement).checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const addSpecialization = () => {
    if (newSpecialization.trim() && formData.specialization) {
      setFormData({
        ...formData,
        specialization: [...formData.specialization, newSpecialization.trim()]
      });
      setNewSpecialization('');
    }
  };
  const removeSpecialization = (index: number) => {
    if (formData.specialization) {
      const updatedSpecializations = [...formData.specialization];
      updatedSpecializations.splice(index, 1);
      setFormData({
        ...formData,
        specialization: updatedSpecializations
      });
    }
  };
  const addBadge = () => {
    if (newBadge.trim()) {
      setFormData({
        ...formData,
        badges: [...(formData.badges || []), newBadge.trim()]
      });
      setNewBadge('');
    }
  };
  const removeBadge = (index: number) => {
    if (formData.badges) {
      const updatedBadges = [...formData.badges];
      updatedBadges.splice(index, 1);
      setFormData({
        ...formData,
        badges: updatedBadges
      });
    }
  };
  const addLanguage = () => {
    if (newLanguage.trim() && formData.languages) {
      setFormData({
        ...formData,
        languages: [...formData.languages, newLanguage.trim()]
      });
      setNewLanguage('');
    }
  };
  const removeLanguage = (index: number) => {
    if (formData.languages) {
      const updatedLanguages = [...formData.languages];
      updatedLanguages.splice(index, 1);
      setFormData({
        ...formData,
        languages: updatedLanguages
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.name || !formData.title || !formData.image || !formData.experience) {
        throw new Error('يرجى ملء جميع الحقول المطلوبة');
      }
      // Validate at least one consultation type is available
      if (!formData.onlineAvailable && !formData.inPersonAvailable) {
        throw new Error('يجب تفعيل نوع واحد على الأقل من الاستشارات');
      }
      // Validate prices for enabled consultation types
      if (formData.onlineAvailable && !formData.price?.online) {
        throw new Error('يرجى تحديد سعر الاستشارة الأونلاين');
      }
      if (formData.inPersonAvailable && !formData.price?.inPerson) {
        throw new Error('يرجى تحديد سعر الاستشارة الوجاهية');
      }
      if (consultant) {
        updateConsultant(consultant.id, formData as Consultant);
      } else {
        addConsultant(formData as Omit<Consultant, 'id'>);
      }
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-24 right-1/2 transform translate-x-1/2 bg-green-100 text-green-800 px-4 py-2 rounded-full shadow-lg z-50 animate-fadeIn';
      notification.textContent = consultant ? 'تم تحديث بيانات الاستشاري بنجاح' : 'تمت إضافة الاستشاري بنجاح';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.add('opacity-0');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'حدث خطأ أثناء حفظ البيانات');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-323">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden" data-id="element-324">
        <div className="p-4 bg-sky-50 border-b border-sky-100 flex justify-between items-center sticky top-0 z-10" data-id="element-325">
          <h3 className="font-bold text-sky-900" data-id="element-326">
            {consultant ? 'تعديل بيانات استشاري' : 'إضافة استشاري جديد'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-sky-100 text-slate-500 hover:text-slate-700 transition-colors" data-id="element-327">
            <XIcon className="h-5 w-5" data-id="element-328" />
          </button>
        </div>
        <div className="overflow-y-auto p-6" style={{
        maxHeight: 'calc(90vh - 70px)'
      }} data-id="element-329">
          <form onSubmit={handleSubmit} data-id="element-330">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-331">
              <div className="col-span-1 md:col-span-2" data-id="element-332">
                <h4 className="text-lg font-medium text-sky-800 mb-4 pb-2 border-b border-sky-100" data-id="element-333">
                  المعلومات الأساسية
                </h4>
              </div>
              <div data-id="element-334">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-335">
                  الاسم
                </label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-336" />
              </div>
              <div data-id="element-337">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-338">
                  اللقب الوظيفي
                </label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-339" />
              </div>
              <div data-id="element-340">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-341">
                  رابط الصورة
                </label>
                <input type="url" name="image" value={formData.image} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="https://example.com/image.jpg" data-id="element-342" />
              </div>
              <div data-id="element-343">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-344">
                  سنوات الخبرة
                </label>
                <input type="text" name="experience" value={formData.experience} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="١٥ سنة خبرة" data-id="element-345" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-346">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-347">
                  نبذة تعريفية
                </label>
                <textarea name="bio" value={formData.bio} onChange={handleChange} required rows={3} className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-348" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-349">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-350">
                  التخصصات
                </label>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-351">
                  {formData.specialization?.map((spec, index) => <div key={index} className="bg-sky-50 text-sky-700 px-3 py-1 rounded-full flex items-center" data-id="element-352">
                      <span data-id="element-353">{spec}</span>
                      <button type="button" onClick={() => removeSpecialization(index)} className="ml-2 text-sky-500 hover:text-sky-700" data-id="element-354">
                        <XIcon className="h-4 w-4" data-id="element-355" />
                      </button>
                    </div>)}
                </div>
                <div className="flex" data-id="element-356">
                  <input type="text" value={newSpecialization} onChange={e => setNewSpecialization(e.target.value)} className="flex-1 px-3 py-2 rounded-l-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="أضف تخصصاً جديداً" data-id="element-357" />
                  <button type="button" onClick={addSpecialization} className="px-3 py-2 bg-sky-600 text-white rounded-r-md hover:bg-sky-700" data-id="element-358">
                    <PlusIcon className="h-5 w-5" data-id="element-359" />
                  </button>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-360">
                <h4 className="text-lg font-medium text-sky-800 mb-4 pb-2 border-b border-sky-100 mt-4" data-id="element-361">
                  معلومات الاستشارة
                </h4>
              </div>
              <div data-id="element-362">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-363">
                  العنوان
                </label>
                <input type="text" name="location" value={formData.location} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-364" />
              </div>
              <div data-id="element-365">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-366">
                  مدة الجلسة
                </label>
                <input type="text" name="duration" value={formData.duration} onChange={handleChange} required className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="30 دقيقة" data-id="element-367" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-368">
                <h4 className="text-lg font-medium text-green-800 mb-4 pb-2 border-b border-green-100 mt-4 flex items-center" data-id="element-369">
                  <MonitorIcon className="h-5 w-5 ml-2" data-id="element-370" />
                  استشارة أونلاين
                </h4>
              </div>
              <div className="flex items-center" data-id="element-371">
                <input type="checkbox" id="onlineAvailable" name="onlineAvailable" checked={formData.onlineAvailable} onChange={handleChange} className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded" data-id="element-372" />
                <label htmlFor="onlineAvailable" className="ml-2 block text-sm text-gray-900" data-id="element-373">
                  متاح للاستشارات الأونلاين
                </label>
              </div>
              <div data-id="element-374">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-375">
                  سعر الاستشارة أونلاين
                </label>
                <input type="text" name="onlinePrice" value={formData.price?.online} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="35 دينار" disabled={!formData.onlineAvailable} data-id="element-376" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-377">
                <h4 className="text-lg font-medium text-purple-800 mb-4 pb-2 border-b border-purple-100 mt-4 flex items-center" data-id="element-378">
                  <UsersIcon className="h-5 w-5 ml-2" data-id="element-379" />
                  استشارة وجاهية (شخصية)
                </h4>
              </div>
              <div className="flex items-center" data-id="element-380">
                <input type="checkbox" id="inPersonAvailable" name="inPersonAvailable" checked={formData.inPersonAvailable} onChange={handleChange} className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-381" />
                <label htmlFor="inPersonAvailable" className="ml-2 block text-sm text-gray-900" data-id="element-382">
                  متاح للاستشارات الوجاهية
                </label>
              </div>
              <div data-id="element-383">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-384">
                  سعر الاستشارة الوجاهية
                </label>
                <input type="text" name="inPersonPrice" value={formData.price?.inPerson} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="50 دينار" disabled={!formData.inPersonAvailable} data-id="element-385" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-386">
                <h4 className="text-lg font-medium text-sky-800 mb-4 pb-2 border-b border-sky-100 mt-4" data-id="element-387">
                  معلومات إضافية
                </h4>
              </div>
              <div data-id="element-388">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-389">
                  التقييم
                </label>
                <input type="number" name="rating" value={formData.rating} onChange={handleChange} min="1" max="5" step="0.1" className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-390" />
              </div>
              <div data-id="element-391">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-392">
                  عدد التقييمات
                </label>
                <input type="number" name="reviewCount" value={formData.reviewCount} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-393" />
              </div>
              <div data-id="element-394">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-395">
                  خصم (إن وجد)
                </label>
                <input type="text" name="discount" value={formData.discount} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="تخفيض 50٪ على جميع الخدمات!" data-id="element-396" />
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-397">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-398">
                  اللغات
                </label>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-399">
                  {formData.languages?.map((lang, index) => <div key={index} className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full flex items-center" data-id="element-400">
                      <span data-id="element-401">{lang}</span>
                      <button type="button" onClick={() => removeLanguage(index)} className="ml-2 text-amber-500 hover:text-amber-700" data-id="element-402">
                        <XIcon className="h-4 w-4" data-id="element-403" />
                      </button>
                    </div>)}
                </div>
                <div className="flex" data-id="element-404">
                  <input type="text" value={newLanguage} onChange={e => setNewLanguage(e.target.value)} className="flex-1 px-3 py-2 rounded-l-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="أضف لغة جديدة" data-id="element-405" />
                  <button type="button" onClick={addLanguage} className="px-3 py-2 bg-amber-600 text-white rounded-r-md hover:bg-amber-700" data-id="element-406">
                    <PlusIcon className="h-5 w-5" data-id="element-407" />
                  </button>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2" data-id="element-408">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-409">
                  شارات خاصة
                </label>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-410">
                  {formData.badges?.map((badge, index) => <div key={index} className="bg-rose-50 text-rose-700 px-3 py-1 rounded-full flex items-center" data-id="element-411">
                      <span data-id="element-412">{badge}</span>
                      <button type="button" onClick={() => removeBadge(index)} className="ml-2 text-rose-500 hover:text-rose-700" data-id="element-413">
                        <XIcon className="h-4 w-4" data-id="element-414" />
                      </button>
                    </div>)}
                </div>
                <div className="flex" data-id="element-415">
                  <input type="text" value={newBadge} onChange={e => setNewBadge(e.target.value)} className="flex-1 px-3 py-2 rounded-l-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" placeholder="أضف شارة جديدة" data-id="element-416" />
                  <button type="button" onClick={addBadge} className="px-3 py-2 bg-rose-600 text-white rounded-r-md hover:bg-rose-700" data-id="element-417">
                    <PlusIcon className="h-5 w-5" data-id="element-418" />
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-4 border-t border-slate-100 flex gap-3" data-id="element-419">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors duration-200" data-id="element-420">
                إلغاء
              </button>
              <button type="submit" disabled={isSubmitting} className="flex-1 py-2.5 rounded-md font-medium bg-sky-600 hover:bg-sky-700 text-white transition-colors duration-200 flex items-center justify-center" data-id="element-421">
                {isSubmitting ? <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin ml-2" data-id="element-422"></div>
                    <span data-id="element-423">جاري الحفظ...</span>
                  </> : <span data-id="element-424">{consultant ? 'تحديث البيانات' : 'إضافة استشاري'}</span>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}